import { useState } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { RevButton, RevIconButton, RevTooltip } from "@revmatics/catalyst";

type Props<TFieldValues extends FieldValues> = {
  children: React.ReactNode;
  isLastItem?: boolean;
  handleDeleteItem: () => void;
  handleAddItem: () => void;
  addItemText?: string;
  control: Control<TFieldValues>;
  name: FieldPath<TFieldValues>;
};

export const TextAreaListItem = <TFieldValues extends FieldValues>({
  children,
  isLastItem = false,
  handleDeleteItem,
  handleAddItem,
  addItemText,
  control,
  name,
}: Props<TFieldValues>) => {
  const [isDeleteTooltipOpen, setIsDeleteTooltipOpen] = useState(false);

  const currentValue = useWatch({ control, name });
  const hasContent =
    typeof currentValue === "string" && currentValue.trim() !== "";

  const handleConfirmDelete = () => {
    if (hasContent) {
      setIsDeleteTooltipOpen(true);
    } else {
      handleDeleteItem();
    }
  };

  const standardPadding = "py-1.5 px-2.5";
  const confirmationPadding = "px-2 py-4";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        {children}
        <RevTooltip
          open={isDeleteTooltipOpen ? true : undefined}
          position="left"
          className={
            isDeleteTooltipOpen ? "w-[248px]" : "w-fit whitespace-nowrap"
          }
          wrapperClassName={
            isDeleteTooltipOpen ? confirmationPadding : standardPadding
          }
          content={
            isDeleteTooltipOpen ? (
              <div className="flex flex-col items-center gap-4">
                <p className="typography-body-sm text-monochrome-900 w-fit text-center">
                  Are you sure you want to delete?
                </p>
                <div className="flex gap-2 h-8">
                  <RevButton
                    buttonSize="small"
                    variant="secondary"
                    text="No, Cancel"
                    handleClick={(e) => {
                      e?.stopPropagation();
                      setIsDeleteTooltipOpen(false);
                    }}
                  />
                  <RevButton
                    buttonSize="small"
                    variant="primary"
                    buttonColor="error"
                    text="Yes, Delete"
                    handleClick={(e) => {
                      e?.stopPropagation();
                      handleDeleteItem();
                      setIsDeleteTooltipOpen(false);
                    }}
                  />
                </div>
              </div>
            ) : (
              "Remove"
            )
          }
        >
          <RevIconButton
            icon={faXmark}
            handleClick={handleConfirmDelete}
            size="s"
          />
        </RevTooltip>
      </div>
      {isLastItem && (
        <div>
          <RevButton
            text={addItemText}
            iconLeft={faPlus}
            handleClick={handleAddItem}
            variant="secondary"
            buttonSize="small"
          />
        </div>
      )}
    </div>
  );
};
